/**
 * A small select-one combobox over a caller-supplied option list.
 *
 * Deliberately dumb about SEARCH: the caller hands in options already filtered
 * for `query`, because the three entity kinds it serves search differently —
 * brands go through `useBrandSearch` (the house pattern for every brand search,
 * typo-tolerant via Algolia), while manufacturers and parent organizations are
 * small enough to match locally.
 */
import { useState } from 'react';

export interface EntityOption {
  id: string;
  label: string;
  /** Secondary text — the id, a country, whatever disambiguates. */
  sub?: string;
}

/** Accent- and case-insensitive substring match over an option's text and id. */
export function optionMatches(option: EntityOption, query: string): boolean {
  const fold = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const q = fold(query.trim());
  if (!q) return true;
  return [option.label, option.sub ?? '', option.id].some((text) => fold(text).includes(q));
}

interface Props {
  value: string;
  /** Resolved display text for `value`, or the raw id when it resolves to nothing. */
  selectedLabel?: string;
  options: EntityOption[];
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (id: string) => void;
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Offer a way back to "none" — for optional pickers only. */
  allowClear?: boolean;
  /** Shown while the option list is still loading. */
  loading?: boolean;
}

const MAX_SHOWN = 20;

export function EntityPicker({
  value,
  selectedLabel,
  options,
  query,
  onQueryChange,
  onSelect,
  placeholder = 'Search…',
  ariaLabel,
  disabled = false,
  allowClear = false,
  loading = false,
}: Props) {
  const [open, setOpen] = useState(false);

  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-sm text-gray-800">
          {selectedLabel || value}
          {selectedLabel && selectedLabel !== value && (
            <span className="ml-1.5 font-mono text-[10px] text-gray-400">{value}</span>
          )}
        </span>
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline disabled:text-gray-300"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Change
        </button>
        {allowClear && (
          <button
            type="button"
            className="text-xs text-red-600 hover:underline disabled:text-gray-300"
            disabled={disabled}
            onClick={() => onSelect('')}
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  const shown = options.slice(0, MAX_SHOWN);
  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        aria-label={ariaLabel}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onQueryChange(e.target.value);
          setOpen(true);
        }}
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      />
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {loading && <li className="px-3 py-2 text-xs text-gray-400">Loading…</li>}
          {!loading && shown.length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-400">No matches</li>
          )}
          {shown.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={option.id === value}
                className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(option.id);
                  onQueryChange('');
                  setOpen(false);
                }}
              >
                <span className="text-gray-800">{option.label}</span>
                {option.sub && <span className="text-[11px] text-gray-400">{option.sub}</span>}
              </button>
            </li>
          ))}
          {options.length > MAX_SHOWN && (
            <li className="px-3 py-1.5 text-[11px] text-gray-400">
              {options.length - MAX_SHOWN} more — keep typing to narrow
            </li>
          )}
          {value && (
            <li className="border-t border-gray-100">
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpen(false)}
              >
                Keep current selection
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
